import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import {
  ArrowLeft,
  Search,
  Trash2,
  Plus,
  Save,
  CheckCircle,
  XCircle,
  RefreshCw,
  FileText,
  Package,
  Calculator,
  Building2,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

const BASE_URL = "https://nissi-india.com/api/openPOAddGst";

/* ─────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────── */
const fmt2 = (v) => parseFloat(v || 0).toFixed(2);
const n = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

const toInputDate = (v) => {
  if (!v) return "";
  const s = String(v);
  if (s.startsWith("1900-01-01")) return "";
  return s.split("T")[0];
};

const pickFirst = (v) => {
  if (Array.isArray(v)) return v[0] ?? "";
  return v ?? "";
};

const findHeadOfficeStoreId = (stores = []) => {
  const ho = stores.find((s) =>
    String(s.fld_storename || "").toLowerCase().includes("head")
  );
  return ho ? String(ho.fld_storeid) : "";
};

function calcItemTotals(item) {
  const qty = n(item.qty);
  const price = n(item.price);
  const ds = n(item.ds);
  const sc = n(item.sc);
  const pf = n(item.pf);
  const cgst = n(item.cgst);
  const sgst = n(item.sgst);
  const igst = n(item.igst);

  const subtotal = qty * price;
  const discountAmt = (subtotal * ds) / 100;
  const afterDiscount = subtotal - discountAmt;

  const igstAmt = (afterDiscount * igst) / 100;
  const cgstAmt = (afterDiscount * cgst) / 100;
  const sgstAmt = (afterDiscount * sgst) / 100;

  const scAmt = (afterDiscount * sc) / 100;
  const pfAmt = (afterDiscount * pf) / 100;

  const total = afterDiscount + igstAmt + cgstAmt + sgstAmt + scAmt + pfAmt;

  return {
    subtotal,
    discountAmt,
    afterDiscount,
    igstAmt,
    cgstAmt,
    sgstAmt,
    scAmt,
    pfAmt,
    total,
  };
}

function recalcFooterTotals(items, charges) {
  let subTotal = 0,
    totalDiscount = 0,
    afterDiscTotal = 0;
  let totalCGST = 0,
    totalSGST = 0,
    totalIGST = 0;

  items.forEach((item) => {
    const t = calcItemTotals(item);
    subTotal += t.subtotal;
    totalDiscount += t.discountAmt;
    afterDiscTotal += t.afterDiscount;
    totalCGST += t.cgstAmt;
    totalSGST += t.sgstAmt;
    totalIGST += t.igstAmt;
  });

  if (charges.frtTax) {
    const base = n(charges.freight);
    totalCGST += (base * n(charges.frtCGST)) / 100;
    totalSGST += (base * n(charges.frtSGST)) / 100;
    totalIGST += (base * n(charges.frtIGST)) / 100;
  }
  if (charges.insTax) {
    const base = n(charges.insurance);
    totalCGST += (base * n(charges.insCGST)) / 100;
    totalSGST += (base * n(charges.insSGST)) / 100;
    totalIGST += (base * n(charges.insIGST)) / 100;
  }
  if (charges.pfTax) {
    const base = n(charges.pf);
    totalCGST += (base * n(charges.pfCGST)) / 100;
    totalSGST += (base * n(charges.pfSGST)) / 100;
    totalIGST += (base * n(charges.pfIGST)) / 100;
  }

  const beforeRound =
    afterDiscTotal +
    n(charges.freight) +
    n(charges.insurance) +
    n(charges.pf) +
    totalCGST +
    totalSGST +
    totalIGST;

  const tcsAmt = (beforeRound * n(charges.tcsPct)) / 100;
  const withTCS = beforeRound + tcsAmt;

  const decimal = withTCS - Math.floor(withTCS);
  const roundOff = decimal < 0.5 ? -decimal : 1 - decimal;
  const grandTotal = withTCS + roundOff;

  return {
    subTotal,
    totalDiscount,
    afterDiscTotal,
    totalCGST,
    totalSGST,
    totalIGST,
    tcsAmt,
    roundOff,
    grandTotal,
  };
}

/* ── Charge row ── */
const ChargeRow = ({
  label,
  valueKey,
  taxKey,
  cgstKey,
  sgstKey,
  igstKey,
  charges,
  taxRates,
  onChange,
  isDark,
  inputClass,
  labelClass,
}) => {
  const hasTax = charges[taxKey];
  return (
    <div className="flex flex-wrap gap-4 items-end mb-4 pb-4 border-b border-dashed border-gray-200 dark:border-slate-700 last:border-0 last:mb-0 last:pb-0">
      <div className="min-w-[140px]">
        <label className={labelClass}>{label} (₹)</label>
        <input
          type="number"
          min="0"
          step="0.01"
          className={`${inputClass} w-32`}
          value={charges[valueKey]}
          onChange={(e) => onChange(valueKey, e.target.value)}
        />
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
        <input
          type="checkbox"
          checked={!!hasTax}
          className="w-4 h-4 rounded"
          onChange={(e) => onChange(taxKey, e.target.checked)}
        />
        <span className={isDark ? "text-gray-300" : "text-gray-600"}>Tax</span>
      </label>

      {hasTax && (
        <>
          {[
            ["CGST%", cgstKey, "cgstRates", "fld_cgstpercent"],
            ["SGST%", sgstKey, "sgstRates", "fld_sgstpercent"],
            ["IGST%", igstKey, "igstRates", "fld_igstpercent"],
          ].map(([lbl, key, rateKey, fld]) => (
            <div key={key} className="min-w-[100px]">
              <label className={labelClass}>{lbl}</label>
              <select
                className={`${inputClass} w-24`}
                value={charges[key]}
                onChange={(e) => onChange(key, e.target.value)}
              >
                <option value="0">0</option>
                {taxRates[rateKey]?.map((r, i) => (
                  <option key={i} value={r[fld]}>
                    {r[fld]}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const OpenMiscOrderAdd = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const branchId = parseInt(localStorage.getItem("branchid") || localStorage.getItem("branchId") || "8", 10);
  const loginUserId = parseInt(localStorage.getItem("loginuserid") || localStorage.getItem("userId") || "37", 10);
  const branchtype =
    localStorage.getItem("branch") === "HEAD-OFFICE" ||
    localStorage.getItem("branchType") === "HO"
      ? "HO"
      : "Branch";

  const isEdit = localStorage.getItem("OpenPurchaseOrderEdit") === "true";
  const editId = localStorage.getItem("SelPurchaseOrderId") || "";

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  /* ── Form state ── */
  const [poType, setPoType] = useState("Open");
  const [poDate, setPoDate] = useState("");
  const [refNo, setRefNo] = useState("");
  const [refDate, setRefDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [supplier, setSupplier] = useState("");
  const [supplierAddr, setSupplierAddr] = useState("");
  const [store, setStore] = useState("");
  const [projCode, setProjCode] = useState("");
  const [projName, setProjName] = useState("");
  const [employee, setEmployee] = useState("");
  const [delivMethod, setDelivMethod] = useState("");
  const [delivPlace, setDelivPlace] = useState("");
  const [delivAddr, setDelivAddr] = useState("");
  const [payTerms, setPayTerms] = useState("");
  const [priceBasics, setPriceBasics] = useState("");
  const [remarks, setRemarks] = useState("");
  const [closePO, setClosePO] = useState(false);
  const [cancelPO, setCancelPO] = useState(false);

  /* ── Dropdown data ── */
  const [supplierList, setSupplierList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [projCodes, setProjCodes] = useState([]);
  const [projNames, setProjNames] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [taxRates, setTaxRates] = useState({
    cgstRates: [],
    sgstRates: [],
    igstRates: [],
  });

  /* ── Item search ── */
  const [category, setCategory] = useState("Select");
  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");
  const [itemList, setItemList] = useState([]);
  const [searchDone, setSearchDone] = useState(false);

  /* ── Maintenance mode ── */
  const [maintItems, setMaintItems] = useState([]);
  const [maintSel, setMaintSel] = useState([]);

  /* ── Selected items & submission ── */
  const [selectedItems, setSelectedItems] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [grnExists, setGrnExists] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ── Charges ── */
  const [charges, setCharges] = useState({
    freight: 0,
    frtTax: false,
    frtCGST: 0,
    frtSGST: 0,
    frtIGST: 0,
    freightDesc: "",
    insurance: 0,
    insTax: false,
    insCGST: 0,
    insSGST: 0,
    insIGST: 0,
    pf: 0,
    pfTax: false,
    pfCGST: 0,
    pfSGST: 0,
    pfIGST: 0,
    tcsPct: 0,
  });

  const liveTotals = submitted ? recalcFooterTotals(selectedItems, charges) : null;

  const onChargeChange = (key, value) => {
    setCharges((prev) => ({ ...prev, [key]: value }));
  };

  /* ════ LOAD ON MOUNT ════ */
  useEffect(() => {
    const init = async () => {
      try {
        const dd = await axios.get(`${BASE_URL}/getDropdowns`, {
          params: { branchid: branchId, branchtype },
        });
        const d = dd.data?.data || {};
        const stores = d.stores || [];
        const suppliers = d.suppliers || [];

        setStoreList(stores);
        setSupplierList(suppliers);
        setCategoryList(d.itemCategories || []);
        setProjCodes(d.projectCodes || []);
        setProjNames(d.projectNames || []);

        if (!isEdit) {
          const hoId = findHeadOfficeStoreId(stores);
          setStore((prev) => prev || hoId || (stores[0] ? String(stores[0].fld_storeid) : ""));
        }

        axios
          .get(`${BASE_URL}/getEmployeeSignature`, { params: { branchid: branchId } })
          .then((r) => setEmployeeList(r.data?.rows || []))
          .catch(() => {});

        axios
          .get(`${BASE_URL}/getTaxRates`)
          .then((r) => {
            if (r.data?.success) setTaxRates(r.data);
          })
          .catch(() => {});

        if (isEdit && editId) {
          await loadEditData(editId, stores, suppliers);
        }
      } catch {
        toast.error("Failed to load dropdowns");
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (poType === "Maintenance" && store) {
      axios
        .get(`${BASE_URL}/getUnderMaintenanceItems`, {
          params: { branchid: branchId, storeid: store },
        })
        .then((r) => setMaintItems(r.data?.rows || []))
        .catch(() => {});
    }
  }, [poType, store, branchId]);

  /* ════ LOAD EDIT DATA ════ */
  const loadEditData = async (poId, storesFromDD = [], suppliersFromDD = []) => {
    try {
      const r = await axios.get(`${BASE_URL}/getOpenPurchaseOrderById`, {
        params: { purchaseorderid: poId },
      });

      if (!r.data?.success) {
        toast.error("Failed to load PO");
        return;
      }

      const m = r.data.master || {};
      setGrnExists(!!r.data.grnExists);

      setPoType(m.fld_type === "UM" ? "Maintenance" : "Open");
      setPoDate(toInputDate(m.fld_Date));
      setDueDate(toInputDate(m.fld_Duedate));
      setRefNo(m.fld_RefNo || "");
      setRefDate(toInputDate(m.fld_RefDate));

      const supId = String(m.fld_supplierid || "");
      setSupplier(supId);

      if (supId && suppliersFromDD?.length) {
        const exists = suppliersFromDD.some(
          (s) => String(s.fld_supplierid) === supId
        );
        if (!exists) {
          setSupplierList((prev) => [
            {
              fld_supplierid: Number(supId),
              fld_suppliername: m.fld_suppliername || `Supplier ${supId}`,
            },
            ...prev,
          ]);
        }
      }

      const addr = r.data?.supplier_address || "";
      setSupplierAddr(addr);
      if (!addr && supId) {
        try {
          const rr = await axios.get(`${BASE_URL}/getSupplierAddress`, {
            params: { supplierid: supId },
          });
          setSupplierAddr(rr.data?.address || "");
        } catch {}
      }

      const fetchedStore = String(m.fld_storeid || "");
      if (fetchedStore) {
        setStore(fetchedStore);
      } else {
        const hoId = findHeadOfficeStoreId(storesFromDD);
        setStore(hoId || (storesFromDD[0] ? String(storesFromDD[0].fld_storeid) : ""));
      }

      setProjCode(String(m.fld_projectid || ""));
      setProjName(String(m.fld_projectid || ""));
      setEmployee(String(m.fld_employeeid || ""));
      setDelivMethod(m.fld_Deliverymethod || "");
      setDelivPlace(m.fld_DeliveryPlace || "");
      setDelivAddr(m.fld_DeliveryAddress || "");
      setPayTerms(m.fld_Payterms || "");
      setPriceBasics(m.fld_pricebasics || "");
      setRemarks(m.fld_Remarks || "");
      setClosePO(!!m.fld_isClosed);
      setCancelPO(!!m.fld_isCanceled);

      setCharges((prev) => ({
        ...prev,
        freight: n(m.fld_frieght),
        frtTax: !!m.fld_frttax,
        frtCGST: n(m.fld_frtcgst),
        frtSGST: n(m.fld_frtsgst),
        frtIGST: n(m.fld_frtigst),
        freightDesc: m.fld_frtdescription || "",
        insurance: n(m.fld_insurance),
        insTax: !!m.fld_instax,
        insCGST: n(m.fld_inscgst),
        insSGST: n(m.fld_inssgst),
        insIGST: n(m.fld_insigst),
        pf: n(m.fld_pf),
        pfTax: !!m.fld_pftax,
        pfCGST: n(m.fld_pfcgst),
        pfSGST: n(m.fld_pfsgst),
        pfIGST: n(m.fld_pfigst),
        tcsPct: n(m.fld_tcspercent),
      }));

      const mapped = (r.data.items || []).map((it) => ({
        fld_itemid: it.fld_Itemid,
        fld_itemcode: it.fld_itemcode,
        fld_itemname: it.fld_itemname,
        fld_itemHSNNo: pickFirst(it.fld_itemHSNNo || it.hsncode || it.fld_itempartno || ""),
        fld_uomname: it.fld_uomname,
        specification: it.fld_specification || "",
        qty: n(it.fld_POQty || it.fld_ReqQty),
        price: n(it.fld_POPrice),
        ds: n(it.fld_DS),
        sc: n(it.fld_SC),
        pf: n(it.fld_PandF),
        cgst: n(it.fld_VAT),
        sgst: n(it.fld_CST),
        igst: n(it.fld_ED),
        isMaintenance: false,
      }));

      setSelectedItems(mapped);
      if (mapped.length) setSubmitted(true);
    } catch {
      toast.error("Error loading PO details");
    }
  };

  const onSupplierChange = async (id) => {
    setSupplier(id);
    setSupplierAddr("");
    if (!id) return;
    try {
      const r = await axios.get(`${BASE_URL}/getSupplierAddress`, {
        params: { supplierid: id },
      });
      setSupplierAddr(r.data?.address || "");
    } catch {}
  };

  const handleItemSearch = () => {
    if (!poDate) {
      toast.warning("Please enter PO Date");
      return;
    }
    if (!store) {
      toast.warning("Please select Store");
      return;
    }
    if (!dueDate) {
      toast.warning("Please enter Due Date");
      return;
    }
    if (!supplier) {
      toast.warning("Please select Supplier");
      return;
    }

    axios
      .get(`${BASE_URL}/getOpenPOItemList`, {
        params: {
          categoryid: category || "Select",
          itemcode: itemCode,
          itemname: itemName,
          branchid: branchId,
          storeid: store,
        },
      })
      .then((r) => {
        setItemList(Array.isArray(r.data.rows) ? r.data.rows : []);
        setSearchDone(true);
        if (!r.data.rows?.length) toast.info("No items found");
      })
      .catch(() => toast.error("Failed to fetch items"));
  };

  const handleAddItem = async (item) => {
    if (selectedItems.some((i) => i.fld_itemid === item.fld_itemid)) {
      toast.warning("Item already added");
      return;
    }

    let price = 0,
      cgst = 0,
      sgst = 0,
      igst = 0,
      ds = 0;

    if (supplier) {
      try {
        const r = await axios.get(`${BASE_URL}/getItemPricesBySupplier`, {
          params: { itemid: item.fld_itemid, supplierid: supplier },
        });
        const d = r.data?.data || {};
        price = n(d.fld_price);
        ds = n(d.fld_DS);
        cgst = n(d.fld_VAT);
        sgst = n(d.fld_CST);
        igst = n(d.fld_ED);
      } catch {}

      try {
        const r = await axios.get(`${BASE_URL}/getSupplierWithTax`, {
          params: { supplierid: supplier },
        });
        const d = r.data?.data || {};
        if (d.fld_cgst) cgst = n(d.fld_cgst);
        if (d.fld_sgst) sgst = n(d.fld_sgst);
        if (d.fld_igst) igst = n(d.fld_igst);
      } catch {}
    }

    setSelectedItems((prev) => [
      ...prev,
      {
        fld_itemid: item.fld_itemid,
        fld_itemcode: item.fld_itemcode,
        fld_itemname: item.fld_itemname,
        fld_itemHSNNo: pickFirst(item.fld_itemHSNNo || item.hsncode || ""),
        fld_uomname: item.fld_uomname,
        specification: "",
        qty: 1,
        price,
        ds,
        sc: 0,
        pf: 0,
        cgst,
        sgst,
        igst,
      },
    ]);

    setSubmitted(false);
  };

  const updateItem = (idx, field, value) => {
    setSelectedItems((prev) => {
      const u = [...prev];
      u[idx] = { ...u[idx], [field]: value };
      return u;
    });
    setSubmitted(false);
  };

  const addMaintItems = () => {
    if (!maintSel.length) {
      toast.warning("Select maintenance items first");
      return;
    }

    const toAdd = maintItems
      .filter(
        (m) =>
          maintSel.includes(m.fld_id) &&
          !selectedItems.some((s) => s.fld_itemid === m.fld_itemid)
      )
      .map((m) => ({
        fld_itemid: m.fld_itemid,
        fld_itemcode: m.fld_itemcode,
        fld_itemname: m.fld_itemname,
        fld_itemHSNNo: pickFirst(m.fld_itemHSNNo || m.hsncode || ""),
        fld_uomname: m.fld_uomname || "",
        specification: "",
        qty: n(m.fld_qty),
        price: 0,
        ds: 0,
        sc: 0,
        pf: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        isMaintenance: true,
      }));

    setSelectedItems((prev) => [...prev, ...toAdd]);
    setSubmitted(false);
  };

const handleSubmitItems = () => {
  if (!selectedItems.length) {
    toast.error("No items to submit");
    return;
  }

  for (const it of selectedItems) {
    // Qty
    if (!n(it.qty) || n(it.qty) <= 0) {
      toast.error(`Valid Qty (> 0) required for: ${it.fld_itemname}`);
      return;
    }

    // HSN
    if (!pickFirst(it.fld_itemHSNNo)?.toString().trim()) {
      toast.error(`HSN is required for: ${it.fld_itemname}`);
      return;
    }

    // Price (allow 0 only for maintenance items)
    if (!it.isMaintenance && n(it.price) < 0) {
      toast.error(`Price cannot be negative for: ${it.fld_itemname}`);
      return;
    }

    // Discount / SC / P&F range
    if (n(it.ds) < 0 || n(it.ds) > 100) {
      toast.error(`DS% must be between 0 and 100 for: ${it.fld_itemname}`);
      return;
    }
    if (n(it.sc) < 0 || n(it.sc) > 100) {
      toast.error(`SC% must be between 0 and 100 for: ${it.fld_itemname}`);
      return;
    }
    if (n(it.pf) < 0 || n(it.pf) > 100) {
      toast.error(`P&F% must be between 0 and 100 for: ${it.fld_itemname}`);
      return;
    }

    // Tax mutual exclusivity
    const hasCGST = n(it.cgst) > 0;
    const hasSGST = n(it.sgst) > 0;
    const hasIGST = n(it.igst) > 0;

    if (hasIGST && (hasCGST || hasSGST)) {
      toast.error(`"${it.fld_itemname}": Cannot use IGST together with CGST/SGST`);
      return;
    }
  }

  setSubmitted(true);
  toast.success("Items submitted! Review totals below.");
};

  const handleSave = async () => {
    if (!poDate) {
      toast.error("PO Date is required");
      return;
    }
    if (!dueDate) {
      toast.error("Due Date is required");
      return;
    }
    if (!supplier) {
      toast.error("Supplier is required");
      return;
    }
    if (!store) {
      toast.error("Store is required");
      return;
    }
    if (!delivMethod) {
      toast.error("Delivery Method is required");
      return;
    }
    if (!delivPlace) {
      toast.error("Delivery Place is required");
      return;
    }
    if (!payTerms) {
      toast.error("Payment Terms are required");
      return;
    }
    if (!priceBasics) {
      toast.error("Price Basics are required");
      return;
    }
    if (!charges.freightDesc) {
      toast.error("Freight Description is required");
      return;
    }
    if (!selectedItems.length) {
      toast.error("Add at least one item");
      return;
    }
    if (!submitted) {
      toast.error("Click Submit Items first");
      return;
    }

    if ((closePO || cancelPO) && !remarks) {
      toast.error("Remarks required when closing/cancelling a PO");
      return;
    }

    for (const it of selectedItems) {
      if (!n(it.qty)) {
        toast.error(`Qty missing: ${it.fld_itemname}`);
        return;
      }
      if (!pickFirst(it.fld_itemHSNNo)) {
        toast.error(`HSN missing: ${it.fld_itemname}`);
        return;
      }
    }

    setSaving(true);

    try {
      const t = recalcFooterTotals(selectedItems, charges);

      const payload = {
        branchid: branchId,
        loginuserid: loginUserId,
        storeid: parseInt(store || 0, 10),
        supplierid: parseInt(supplier || 0, 10),
        podate: poDate,
        duedate: dueDate,
        refno: refNo,
        refdate: refDate,
        projectid: parseInt(projCode || 0, 10),
        employeeid: parseInt(employee || 0, 10),
        deliverymethod: delivMethod,
        deliveryplace: delivPlace,
        deliveryaddress: delivAddr,
        payterms: payTerms,
        pricebasics: priceBasics,
        remarks,
        freightdesc: charges.freightDesc,
        freight: n(charges.freight),
        insurance: n(charges.insurance),
        pf: n(charges.pf),
        frttax: charges.frtTax ? 1 : 0,
        frtcgst: n(charges.frtCGST),
        frtsgst: n(charges.frtSGST),
        frtigst: n(charges.frtIGST),
        instax: charges.insTax ? 1 : 0,
        inscgst: n(charges.insCGST),
        inssgst: n(charges.insSGST),
        insigst: n(charges.insIGST),
        pftax: charges.pfTax ? 1 : 0,
        pfcgst: n(charges.pfCGST),
        pfsgst: n(charges.pfSGST),
        pfigst: n(charges.pfIGST),
        tcspercent: n(charges.tcsPct),
        tcsamt: t.tcsAmt,
        transport: t.roundOff,
        total: t.grandTotal,
        subtotal: t.subTotal,
        cgstamt: t.totalCGST,
        sgstamt: t.totalSGST,
        igstamt: t.totalIGST,
        discountamt: t.totalDiscount,
        potype: poType === "Maintenance" ? "UM" : "OP",
        closechecked: closePO ? 1 : 0,
        cancelchecked: cancelPO ? 1 : 0,
        items: selectedItems.map((it) => ({
          itemid: it.fld_itemid,
          qty: n(it.qty),
          price: n(it.price),
          ds: n(it.ds),
          sc: n(it.sc),
          pf: n(it.pf),
          cgst: n(it.cgst),
          sgst: n(it.sgst),
          igst: n(it.igst),
          specification: it.specification || "",
          hsncode: pickFirst(it.fld_itemHSNNo) || "",
        })),
      };

      const r =
        isEdit && editId
          ? await axios.put(`${BASE_URL}/updateOpenPurchaseOrder`, {
              ...payload,
              purchaseorderid: parseInt(editId, 10),
            })
          : await axios.post(`${BASE_URL}/insertOpenPurchaseOrder`, payload);

      if (r.data?.success) {
        toast.success(
          `Purchase Order ${isEdit ? "updated" : "saved"} successfully!`
        );
        localStorage.removeItem("OpenPurchaseOrderEdit");
        localStorage.removeItem("SelPurchaseOrderId");
        localStorage.removeItem("SelPOStatus");
        setTimeout(() => navigate("/inventory/OpenPurchaseOrder_Listing"), 1500);
      } else {
        toast.error(r.data?.message || "Save failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  /* ── Render ── */
  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            {isEdit ? "Edit Open / Misc PO" : "Add Open / Misc Purchase Order"}
          </span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Top Header */}
        <div
          className={`p-6 rounded-2xl shadow-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {isEdit ? "Edit Purchase Order" : "New Open / Misc PO"}
              </h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {grnExists ? "GRN exists — fields locked" : "Create or update open / maintenance PO"}
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate("/inventory/OpenpurchaseOrderListing")}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
              isDark
                ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                : "border-slate-300 text-slate-700 hover:bg-slate-100"
            }`}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>

        {/* 1. PO Type + Header */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <Building2 size={20} className={`text-${baseColor}-600`} />
              <h3 className="text-xl font-bold">PO Header</h3>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* PO Type */}
            <div>
              <label className={labelClass}>PO Type</label>
              <div className="flex gap-2">
                {["Open", "Maintenance"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    disabled={grnExists}
                    onClick={() => setPoType(t)}
                    className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                      poType === t
                        ? `${buttonClass} ${hoverClass} text-white shadow`
                        : isDark
                        ? "bg-slate-800 text-slate-300 border border-slate-600"
                        : "bg-gray-100 text-gray-700 border border-gray-200"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <div>
                <label className={labelClass}>
                  PO Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className={inputClass}
                  value={poDate}
                  disabled={grnExists}
                  onChange={(e) => setPoDate(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Due Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className={inputClass}
                  value={dueDate}
                  disabled={grnExists}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Ref No</label>
                <input
                  type="text"
                  className={inputClass}
                  value={refNo}
                  disabled={grnExists}
                  onChange={(e) => setRefNo(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Ref Date</label>
                <input
                  type="date"
                  className={inputClass}
                  value={refDate}
                  disabled={grnExists}
                  onChange={(e) => setRefDate(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Supplier <span className="text-red-500">*</span>
                </label>
                <select
                  className={inputClass}
                  value={supplier}
                  disabled={grnExists}
                  onChange={(e) => onSupplierChange(e.target.value)}
                >
                  <option value="">Select Supplier</option>
                  {supplierList.map((s) => (
                    <option key={s.fld_supplierid} value={s.fld_supplierid}>
                      {s.fld_suppliername}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Supplier Address</label>
                <input
                  type="text"
                  className={inputClass}
                  value={supplierAddr}
                  readOnly
                />
              </div>
              <div>
                <label className={labelClass}>
                  Store <span className="text-red-500">*</span>
                </label>
                <select
                  className={inputClass}
                  value={store}
                  disabled={grnExists}
                  onChange={(e) => setStore(e.target.value)}
                >
                  <option value="">Select Store</option>
                  {storeList.map((s) => (
                    <option key={s.fld_storeid} value={s.fld_storeid}>
                      {s.fld_storename}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Project Code</label>
                <select
                  className={inputClass}
                  value={projCode}
                  disabled={grnExists}
                  onChange={(e) => {
                    setProjCode(e.target.value);
                    setProjName(e.target.value);
                  }}
                >
                  <option value="">Select</option>
                  {projCodes.map((p, i) => (
                    <option
                      key={p.fld_projectid ?? i}
                      value={p.fld_projectid ?? p.fld_contractid ?? ""}
                    >
                      {p.fld_projectcode || p.fld_contractname || p.fld_projectname}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Employee</label>
                <select
                  className={inputClass}
                  value={employee}
                  disabled={grnExists}
                  onChange={(e) => setEmployee(e.target.value)}
                >
                  <option value="">Select</option>
                  {employeeList.map((e) => (
                    <option key={e.fld_employeeid} value={e.fld_employeeid}>
                      {e.fld_employee}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  Delivery Method <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={inputClass}
                  value={delivMethod}
                  disabled={grnExists}
                  placeholder="e.g. By Road"
                  onChange={(e) => setDelivMethod(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Delivery Place <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={inputClass}
                  value={delivPlace}
                  disabled={grnExists}
                  placeholder="Delivery location"
                  onChange={(e) => setDelivPlace(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Delivery Address</label>
                <input
                  type="text"
                  className={inputClass}
                  value={delivAddr}
                  disabled={grnExists}
                  onChange={(e) => setDelivAddr(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Payment Terms <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={inputClass}
                  value={payTerms}
                  disabled={grnExists}
                  placeholder="e.g. 30 Days Net"
                  onChange={(e) => setPayTerms(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Price Basics <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={inputClass}
                  value={priceBasics}
                  disabled={grnExists}
                  placeholder="e.g. Ex-Works, CIF"
                  onChange={(e) => setPriceBasics(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Maintenance items */}
        {poType === "Maintenance" && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-xl font-bold">Under Maintenance — Select Spares</h3>
              </div>
            </div>
            <div className="p-6">
              {!store ? (
                <p className="text-sm text-center py-4 text-gray-500">Select a store first.</p>
              ) : maintItems.length === 0 ? (
                <p className="text-sm text-center py-4 text-gray-500">
                  No maintenance items found for this store.
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead
                        className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                      >
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase">Sel</th>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase">DC No</th>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Code</th>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Name</th>
                          <th className="px-4 py-3 text-right text-xs font-bold uppercase">Qty</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                        {maintItems.map((item, idx) => (
                          <tr
                            key={idx}
                            className={
                              isDark
                                ? "bg-gray-800/20 hover:bg-gray-700/30"
                                : "bg-white hover:bg-gray-50"
                            }
                          >
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                className="w-4 h-4 rounded"
                                checked={maintSel.includes(item.fld_id)}
                                onChange={(e) =>
                                  setMaintSel((prev) =>
                                    e.target.checked
                                      ? [...prev, item.fld_id]
                                      : prev.filter((id) => id !== item.fld_id)
                                  )
                                }
                              />
                            </td>
                            <td className="px-4 py-3">{item.fld_dcno}</td>
                            <td className="px-4 py-3 font-mono text-xs">{item.fld_itemcode}</td>
                            <td className="px-4 py-3">{item.fld_itemname}</td>
                            <td className="px-4 py-3 text-right">{item.fld_qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={addMaintItems}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
                    >
                      <Plus size={16} />
                      Add Selected
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Item Search (Open) */}
        {poType === "Open" && !grnExists && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <Search size={20} className={`text-${baseColor}-600`} />
                <h3 className="text-xl font-bold">Item Search</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
                <div>
                  <label className={labelClass}>Category</label>
                  <select
                    className={inputClass}
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="Select">All Categories</option>
                    {categoryList.map((c) => (
                      <option key={c.fld_itemcategoryid} value={c.fld_itemcategoryid}>
                        {c.fld_itemcategory_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Item Code</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={itemCode}
                    placeholder="Search by code…"
                    onChange={(e) => setItemCode(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClass}>Item Name</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={itemName}
                    placeholder="Search by name…"
                    onChange={(e) => setItemName(e.target.value)}
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleItemSearch}
                    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
                  >
                    <Search size={16} />
                    Search
                  </button>
                </div>
              </div>

              {searchDone &&
                (itemList.length === 0 ? (
                  <p className="text-center py-8 text-sm text-gray-500">No items found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead
                        className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                      >
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase">Add</th>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Code</th>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Name</th>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase">HSN No</th>
                          <th className="px-4 py-3 text-left text-xs font-bold uppercase">UOM</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                        {itemList.map((item, idx) => {
                          const added = selectedItems.some(
                            (i) => i.fld_itemid === item.fld_itemid
                          );
                          return (
                            <tr
                              key={idx}
                              className={`${
                                isDark
                                  ? "bg-gray-800/20 hover:bg-gray-700/30"
                                  : "bg-white hover:bg-gray-50"
                              } ${added ? "opacity-50" : ""}`}
                            >
                              <td className="px-4 py-3">
                                <button
                                  disabled={added}
                                  onClick={() => handleAddItem(item)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                    added
                                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                      : `${buttonClass} ${hoverClass} text-white`
                                  }`}
                                >
                                  {added ? "Added" : "+ Add"}
                                </button>
                              </td>
                              <td className="px-4 py-3 font-mono text-xs">{item.fld_itemcode}</td>
                              <td className="px-4 py-3 font-medium">{item.fld_itemname}</td>
                              <td className="px-4 py-3 text-gray-500">
                                {pickFirst(item.fld_itemHSNNo || item.hsncode || "")}
                              </td>
                              <td className="px-4 py-3 text-gray-500">{item.fld_uomname}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Selected Items */}
        {selectedItems.length > 0 && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <Package size={20} className={`text-${baseColor}-600`} />
                <h3 className="text-xl font-bold">Selected Items</h3>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                    isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {selectedItems.length} item{selectedItems.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
<table className="w-full text-sm min-w-[1450px]">
  <thead
    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
  >
    <tr>
      {!grnExists && (
        <th className="px-3 py-3 text-center text-xs font-bold uppercase">Del</th>
      )}
      <th className="px-3 py-3 text-left text-xs font-bold uppercase">Code</th>
      <th className="px-3 py-3 text-left text-xs font-bold uppercase">Name</th>
      <th className="px-3 py-3 text-left text-xs font-bold uppercase">HSN</th>
      <th className="px-3 py-3 text-left text-xs font-bold uppercase">UOM</th>
      <th className="px-3 py-3 text-left text-xs font-bold uppercase">Spec</th>
      <th className="px-3 py-3 text-right text-xs font-bold uppercase">Qty *</th>
      <th className="px-3 py-3 text-right text-xs font-bold uppercase">Price</th>
      <th className="px-3 py-3 text-right text-xs font-bold uppercase">DS%</th>
      <th className="px-3 py-3 text-right text-xs font-bold uppercase">CGST%</th>
      <th className="px-3 py-3 text-right text-xs font-bold uppercase">SGST%</th>
      <th className="px-3 py-3 text-right text-xs font-bold uppercase">IGST%</th>
      <th className="px-3 py-3 text-right text-xs font-bold uppercase">SC%</th>
      <th className="px-3 py-3 text-right text-xs font-bold uppercase">P&F%</th>
      {submitted && (
        <th className="px-3 py-3 text-right text-xs font-bold uppercase bg-green-700">
          Total ₹
        </th>
      )}
    </tr>
  </thead>

  <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
    {selectedItems.map((item, idx) => {
      const t = submitted ? calcItemTotals(item) : null;
      return (
        <tr
          key={idx}
          className={
            isDark
              ? "bg-gray-800/20 hover:bg-gray-700/30"
              : "bg-white hover:bg-gray-50"
          }
        >
          {!grnExists && (
            <td className="px-3 py-2 text-center">
              <button
                onClick={() =>
                  setSelectedItems((prev) => prev.filter((_, i) => i !== idx))
                }
                className="p-1.5 rounded-full text-red-500 hover:bg-red-100 transition"
              >
                <Trash2 size={14} />
              </button>
            </td>
          )}

          <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
            {item.fld_itemcode}
          </td>

          <td className="px-3 py-2 font-medium">{item.fld_itemname}</td>

          {/* HSN - wider */}
          <td className="px-3 py-2">
            <input
              type="text"
              disabled={grnExists}
              value={pickFirst(item.fld_itemHSNNo)}
              className={`${inputClass} w-28 py-2 text-xs`}
              onChange={(e) => updateItem(idx, "fld_itemHSNNo", e.target.value)}
            />
          </td>

          <td className="px-3 py-2">{item.fld_uomname}</td>

          {/* Spec - wider */}
          <td className="px-3 py-2">
            <input
              type="text"
              disabled={grnExists}
              value={item.specification}
              className={`${inputClass} w-32 py-2 text-xs`}
              onChange={(e) => updateItem(idx, "specification", e.target.value)}
            />
          </td>

          {/* Qty - wider */}
          <td className="px-3 py-2">
            <input
              type="number"
              min="0"
              step="0.01"
              disabled={item.isMaintenance || grnExists}
              value={item.qty}
              className={`${inputClass} w-24 py-2 text-xs text-right`}
              onChange={(e) => updateItem(idx, "qty", e.target.value)}
            />
          </td>

          {/* Price - wider */}
          <td className="px-3 py-2">
            <input
              type="number"
              min="0"
              step="0.01"
              disabled={grnExists}
              value={item.price}
              className={`${inputClass} w-28 py-2 text-xs text-right`}
              onChange={(e) => updateItem(idx, "price", e.target.value)}
            />
          </td>

          {/* DS% - wider */}
          <td className="px-3 py-2">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              disabled={grnExists}
              value={item.ds}
              className={`${inputClass} w-20 py-2 text-xs text-right`}
              onChange={(e) => updateItem(idx, "ds", e.target.value)}
            />
          </td>

          {/* CGST% - wider */}
          <td className="px-3 py-2">
            <select
              disabled={grnExists}
              value={item.cgst}
              className={`${inputClass} w-24 py-2 text-xs`}
              onChange={(e) => updateItem(idx, "cgst", e.target.value)}
            >
              <option value="0">0</option>
              {taxRates.cgstRates.map((r, i) => (
                <option key={i} value={r.fld_cgstpercent}>
                  {r.fld_cgstpercent}
                </option>
              ))}
            </select>
          </td>

          {/* SGST% - wider */}
          <td className="px-3 py-2">
            <select
              disabled={grnExists}
              value={item.sgst}
              className={`${inputClass} w-24 py-2 text-xs`}
              onChange={(e) => updateItem(idx, "sgst", e.target.value)}
            >
              <option value="0">0</option>
              {taxRates.sgstRates.map((r, i) => (
                <option key={i} value={r.fld_sgstpercent}>
                  {r.fld_sgstpercent}
                </option>
              ))}
            </select>
          </td>

          {/* IGST% - wider */}
          <td className="px-3 py-2">
            <select
              disabled={grnExists}
              value={item.igst}
              className={`${inputClass} w-24 py-2 text-xs`}
              onChange={(e) => updateItem(idx, "igst", e.target.value)}
            >
              <option value="0">0</option>
              {taxRates.igstRates.map((r, i) => (
                <option key={i} value={r.fld_igstpercent}>
                  {r.fld_igstpercent}
                </option>
              ))}
            </select>
          </td>

          {/* SC% - wider */}
          <td className="px-3 py-2">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              disabled={grnExists}
              value={item.sc}
              className={`${inputClass} w-20 py-2 text-xs text-right`}
              onChange={(e) => updateItem(idx, "sc", e.target.value)}
            />
          </td>

          {/* P&F% - wider */}
          <td className="px-3 py-2">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              disabled={grnExists}
              value={item.pf}
              className={`${inputClass} w-20 py-2 text-xs text-right`}
              onChange={(e) => updateItem(idx, "pf", e.target.value)}
            />
          </td>

          {submitted && (
            <td className="px-3 py-2 text-right font-bold text-green-600 whitespace-nowrap">
              ₹ {fmt2(t?.total)}
            </td>
          )}
        </tr>
      );
    })}
  </tbody>
</table>
            </div>

            {!grnExists && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={handleSubmitItems}
                  className={`inline-flex items-center gap-2 px-6 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
                >
                  <CheckCircle size={16} />
                  Submit Items
                </button>
              </div>
            )}
          </div>
        )}

        {/* Charges */}
        {submitted && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <Calculator size={20} className={`text-${baseColor}-600`} />
                <h3 className="text-xl font-bold">Freight, Insurance & P&F Charges</h3>
              </div>
            </div>
            <div className="p-6">
              <ChargeRow
                label="Freight"
                valueKey="freight"
                taxKey="frtTax"
                cgstKey="frtCGST"
                sgstKey="frtSGST"
                igstKey="frtIGST"
                charges={charges}
                taxRates={taxRates}
                onChange={onChargeChange}
                isDark={isDark}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <ChargeRow
                label="Insurance"
                valueKey="insurance"
                taxKey="insTax"
                cgstKey="insCGST"
                sgstKey="insSGST"
                igstKey="insIGST"
                charges={charges}
                taxRates={taxRates}
                onChange={onChargeChange}
                isDark={isDark}
                inputClass={inputClass}
                labelClass={labelClass}
              />
              <ChargeRow
                label="P & F"
                valueKey="pf"
                taxKey="pfTax"
                cgstKey="pfCGST"
                sgstKey="pfSGST"
                igstKey="pfIGST"
                charges={charges}
                taxRates={taxRates}
                onChange={onChargeChange}
                isDark={isDark}
                inputClass={inputClass}
                labelClass={labelClass}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
                <div>
                  <label className={labelClass}>
                    Freight Description <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={charges.freightDesc}
                    placeholder="e.g. By Road, By Air"
                    onChange={(e) => onChargeChange("freightDesc", e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClass}>TCS %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    className={inputClass}
                    value={charges.tcsPct}
                    onChange={(e) => onChargeChange("tcsPct", e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Order Summary */}
        {submitted && liveTotals && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-xl font-bold">Order Summary</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {[
                  ["Sub Total", liveTotals.subTotal],
                  ["Discount", liveTotals.totalDiscount],
                  ["After Discount", liveTotals.afterDiscTotal],
                  ["CGST Total", liveTotals.totalCGST],
                  ["SGST Total", liveTotals.totalSGST],
                  ["IGST Total", liveTotals.totalIGST],
                  ["TCS Amount", liveTotals.tcsAmt],
                  ["Round Off", liveTotals.roundOff],
                ].map(([lbl, val]) => (
                  <div
                    key={lbl}
                    className={`rounded-xl p-4 ${
                      isDark ? "bg-slate-800/40" : "bg-gray-50"
                    }`}
                  >
                    <p className={`text-xs font-medium mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {lbl}
                    </p>
                    <p className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-800"}`}>
                      ₹ {fmt2(val)}
                    </p>
                  </div>
                ))}
              </div>

              <div
                className={`rounded-xl p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-800 text-white`}
              >
                <div>
                  <p className="text-sm font-medium opacity-80">
                    Grand Total (Incl. Round Off)
                  </p>
                  <p className="text-3xl font-extrabold mt-1">
                    ₹ {fmt2(liveTotals.grandTotal)}
                  </p>
                </div>
                <div className="text-sm opacity-80 space-y-0.5">
                  <p>
                    {selectedItems.length} item{selectedItems.length !== 1 ? "s" : ""}
                  </p>
                  <p>Round Off: ₹ {fmt2(liveTotals.roundOff)}</p>
                  <p>TCS: ₹ {fmt2(liveTotals.tcsAmt)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Remarks & Status */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Remarks & Order Status</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>
                  Remarks {(closePO || cancelPO) && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  rows={4}
                  className={`${inputClass} resize-none`}
                  value={remarks}
                  placeholder="Enter remarks here..."
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
              <div className="space-y-4 flex flex-col justify-center">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded"
                    checked={closePO}
                    onChange={(e) => {
                      setClosePO(e.target.checked);
                      if (e.target.checked) setCancelPO(false);
                    }}
                  />
                  <span className={`text-sm font-medium flex items-center gap-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <CheckCircle size={16} className="text-green-600" />
                    Close Purchase Order
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded"
                    checked={cancelPO}
                    onChange={(e) => {
                      setCancelPO(e.target.checked);
                      if (e.target.checked) setClosePO(false);
                    }}
                  />
                  <span className={`text-sm font-medium flex items-center gap-1.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <XCircle size={16} className="text-red-500" />
                    Cancel Purchase Order
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Save */}
        {!grnExists && (
          <div className="flex justify-end gap-4 pb-8">
            <button
              onClick={() => navigate("/inventory/OpenpurchaseOrderListing")}
              className={`px-6 py-3 rounded-xl border text-sm font-medium transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`inline-flex items-center gap-2 px-8 py-3 text-white text-sm font-bold rounded-xl shadow-lg transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
            >
              {saving ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
              {saving ? "Saving…" : isEdit ? "Update Purchase Order" : "Save Purchase Order"}
            </button>
          </div>
        )}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default OpenMiscOrderAdd;